import { v4 as uuidv4 } from 'uuid';

import {
  createQueuedReceiptUploads,
  isAmbiguousReceiptRequestError,
  type QueuedReceiptUpload,
  receiptsActions,
  withReceiptUploadDeadline,
} from 'app-components/flux/receipts';
import { receiptStorageSlice } from 'app-components/flux/storage/receiptStorageSlice';
import { getLogger } from 'app-components/utils/core';

import type { AppDispatch } from 'store';
import { promoteLocalReceiptPreview, registerLocalReceiptPreview } from 'utils/receiptLocalPreview';

const log = getLogger('ReceiptCenter/uploadReceipt');

export type UploadReceiptInput = {
  dispatch: AppDispatch;
  localId: string;
  fileUri: string;
  fileName: string;
  mimeType: string;
  now?: () => number;
  pendingReceiptAlreadyAdded?: boolean;
  releaseFileUri?: () => void;
  abortSignal?: AbortSignal;
};

type AbortableAsyncRequest<T> = {
  abort: () => void;
  unwrap: () => Promise<T>;
};

const unwrapWithAbortSignal = async <T>(request: AbortableAsyncRequest<T>, signal?: AbortSignal): Promise<T> => {
  if (!signal) {
    return request.unwrap();
  }

  const abortRequest = (): void => request.abort();
  if (signal.aborted) {
    abortRequest();
  } else {
    signal.addEventListener('abort', abortRequest, { once: true });
  }
  try {
    return await request.unwrap();
  } finally {
    signal.removeEventListener('abort', abortRequest);
  }
};

type StageReceiptUploadsInput = {
  dispatch: (action: ReturnType<typeof receiptsActions.addPendingReceipt>) => unknown;
  files: ReadonlyArray<File>;
  now?: () => number;
  createFileUri?: (file: File) => string;
};

type StageReceiptReuploadInput = {
  dispatch: (action: ReturnType<typeof receiptsActions.addPendingReceipt>) => unknown;
  localId: string;
  fileUri: string;
  fileName: string;
  mimeType: string;
  now?: () => number;
};

export const generateLocalReceiptId = (): string => `local-${uuidv4()}`;

const addQueuedReceipt = (
  dispatch: (action: ReturnType<typeof receiptsActions.addPendingReceipt>) => unknown,
  upload: QueuedReceiptUpload,
): void => {
  dispatch(
    receiptsActions.addPendingReceipt({
      id: upload.localId,
      fileUri: upload.fileUri,
      fileName: upload.fileName,
      mimeType: upload.mimeType,
      createdAt: upload.createdAt,
      localUploadOrder: upload.localUploadOrder,
      isUploadQueued: true,
    }),
  );
};

export const stageReceiptUploads = ({
  dispatch,
  files,
  now = Date.now,
  createFileUri = (file) => URL.createObjectURL(file),
}: StageReceiptUploadsInput): ReadonlyArray<QueuedReceiptUpload> => {
  const uploads = createQueuedReceiptUploads(
    files.map((file) => {
      const fileUri = createFileUri(file);
      registerLocalReceiptPreview(fileUri, file);
      return { fileUri, fileName: file.name, mimeType: file.type };
    }),
    generateLocalReceiptId,
    now,
  );
  uploads.forEach((upload) => {
    addQueuedReceipt(dispatch, upload);
  });
  return uploads;
};

export const stageReceiptReupload = ({
  dispatch,
  localId,
  fileUri,
  fileName,
  mimeType,
  now = Date.now,
}: StageReceiptReuploadInput): QueuedReceiptUpload => {
  const [upload] = createQueuedReceiptUploads([{ fileUri, fileName, mimeType }], () => localId, now);
  if (!upload) {
    throw new Error('Failed to stage receipt reupload.');
  }
  addQueuedReceipt(dispatch, upload);
  return upload;
};

export const uploadReceiptFromFile = async ({
  dispatch,
  localId,
  fileUri,
  fileName,
  mimeType,
  now = Date.now,
  pendingReceiptAlreadyAdded = false,
  releaseFileUri,
  abortSignal,
}: UploadReceiptInput): Promise<void> => {
  if (abortSignal?.aborted) {
    return;
  }
  const startedAt = now();
  if (pendingReceiptAlreadyAdded) {
    dispatch(receiptsActions.markReceiptUploadStarted({ id: localId, now: startedAt }));
  } else {
    dispatch(
      receiptsActions.addPendingReceipt({
        id: localId,
        fileUri,
        fileName,
        mimeType,
        createdAt: startedAt,
      }),
    );
  }

  try {
    const uploadRequest = dispatch(
      receiptStorageSlice.actions.uploadNewDocument({
        name: fileName,
        mimeType,
        fileUri,
      }),
    );
    const document = await withReceiptUploadDeadline(unwrapWithAbortSignal(uploadRequest, abortSignal), {
      onTimeout: uploadRequest.abort,
    });

    if (abortSignal?.aborted) {
      return;
    }
    if (!document.id) {
      throw new Error('Receipt upload did not return a document id.');
    }

    promoteLocalReceiptPreview(fileUri, document.id);
    dispatch(
      receiptsActions.markReceiptCreationPending({
        id: localId,
        documentId: document.id,
        documentName: document.name ?? fileName,
        mimeType: document.mimeType ?? mimeType,
      }),
    );
    releaseFileUri?.();

    try {
      await unwrapWithAbortSignal(
        dispatch(
          receiptsActions.createReceipt({
            documentId: document.id,
            name: document.name ?? fileName,
          }),
        ),
        abortSignal,
      );
    } catch (error) {
      if (abortSignal?.aborted) {
        return;
      }
      const creationPayload = {
        id: localId,
        documentId: document.id,
        documentName: document.name ?? fileName,
        mimeType: document.mimeType ?? mimeType,
      };
      if (!isAmbiguousReceiptRequestError(error)) {
        log.warn('Receipt creation failed after storage upload:', error);
        dispatch(receiptsActions.markReceiptCreationFailed(creationPayload));
        return;
      }

      log.warn('Receipt creation returned an uncertain result after storage upload:', error);
      dispatch(receiptsActions.markReceiptCreationUncertain(creationPayload));

      try {
        await unwrapWithAbortSignal(dispatch(receiptsActions.fetchReceipts(undefined)), abortSignal);
      } catch (reconciliationError) {
        if (abortSignal?.aborted) {
          return;
        }
        log.warn('Immediate receipt creation reconciliation failed:', reconciliationError);
      }
    }
  } catch (error) {
    if (abortSignal?.aborted) {
      return;
    }
    log.error('Failed to upload receipt:', error);
    dispatch(receiptsActions.markReceiptUploadFailed(localId));
  }
};
